/**
 * useTemplates Hook
 *
 * Client-side service layer for template discovery, search, and filtering.
 * Consumes templates from templateService, which is the single source of
 * truth for template data (AutoTemplates fetched from storage).
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import { CATEGORY_DISPLAY, TEMPLATE_CATEGORIES } from '@/types/template';
import type { TemplateDefinition, TemplateManifest } from '@/types/template';
import { FEATURED_TEMPLATE_IDS } from '@/data/templates';
import { getAllTemplatesCached } from '@/services/templateService';
import { useImportedTemplateStore } from '@/stores/importedTemplateStore';

// =============================================================================
// TYPES
// =============================================================================

export interface UseTemplatesOptions {
  /** Filter by category */
  category?: string;
  /** Search query (fuzzy matched against name, description, tags, category) */
  searchQuery?: string;
  /** Only show featured templates */
  featuredOnly?: boolean;
  /**
   * Sort order for results. Ignored when a search query is active (Fuse.js
   * relevance ranking takes precedence). Defaults to `'featured'`.
   */
  sortBy?: 'featured' | 'name-asc' | 'name-desc';
  /** Whether to enable template fetching (passed to useQuery). Defaults to true. */
  enabled?: boolean;
}

export interface TemplateCategory {
  key: string;
  label: string;
  count: number;
}

export interface UseTemplatesResult {
  /** Filtered + searched template manifests (metadata only) */
  templates: TemplateManifest[];
  /** All available categories with their display names, prefixed with an "All" entry */
  categories: TemplateCategory[];
  /** Get the full template definition (with workflow) by ID */
  getTemplate: (id: string) => TemplateDefinition | undefined;
  /** Total number of templates before filtering */
  totalCount: number;
  /** Whether a search query is currently active */
  isSearching: boolean;
  /** Whether templates are still loading from the service */
  isLoading: boolean;
  /** Error from template fetch, if any */
  error: Error | null;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Sort templates by the given mode. */
function sortTemplates(
  items: TemplateManifest[],
  mode: 'featured' | 'name-asc' | 'name-desc' = 'featured',
): TemplateManifest[] {
  return [...items].sort((a, b) => {
    switch (mode) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'featured':
      default: {
        const aFeatured = a.featuredOrder ?? Infinity;
        const bFeatured = b.featuredOrder ?? Infinity;
        if (aFeatured !== bFeatured) return aFeatured - bFeatured;
        return a.name.localeCompare(b.name);
      }
    }
  });
}

/**
 * Derive manifest-only views from full template definitions.
 * Strips heavy runtime fields (workflowDefinition, connectionsConfiguration,
 * parameters) and applies featured ordering from `FEATURED_TEMPLATE_IDS`.
 */
function toManifests(templates: TemplateDefinition[]): TemplateManifest[] {
  return templates.map(
    ({ workflowDefinition: _wd, connectionsConfiguration: _cc, parameters: _p, ...manifest }) => {
      const featuredIdx = FEATURED_TEMPLATE_IDS.indexOf(
        manifest.id as (typeof FEATURED_TEMPLATE_IDS)[number],
      );
      if (featuredIdx !== -1) {
        return { ...manifest, featuredOrder: featuredIdx + 1 };
      }
      // Strip any stale featuredOrder that may exist in the source data
      const { featuredOrder: _fo, ...rest } = manifest;
      return rest;
    },
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useTemplates(options: UseTemplatesOptions = {}): UseTemplatesResult {
  const { category, featuredOnly, searchQuery, sortBy = 'featured', enabled = true } = options;
  const trimmedQuery = searchQuery?.trim() ?? '';
  const isSearching = trimmedQuery.length > 0;

  // Fetch templates from storage via the service's deduped cache.
  const {
    data: fetchedTemplates = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['templates'],
    queryFn: () => getAllTemplatesCached(),
    enabled: enabled ?? true,
    staleTime: 60 * 60 * 1000, // Storage content changes rarely; refresh hourly
    retry: 1,
  });

  // Session-scoped templates imported via the gallery "Import from JSON" card.
  // Imported entries take precedence over a fetched template with the same id
  // so re-importing a tweaked copy of a built-in template tests the new version.
  const importedEntries = useImportedTemplateStore((s) => s.entries);
  const allTemplates = useMemo(() => {
    const imported = Object.values(importedEntries).map((e) => e.template);
    if (imported.length === 0) return fetchedTemplates;
    const importedIds = new Set(imported.map((t) => t.id));
    return [...imported, ...fetchedTemplates.filter((t) => !importedIds.has(t.id))];
  }, [importedEntries, fetchedTemplates]);

  // Derive manifests (metadata-only) from the full definitions
  const allManifests = useMemo(() => toManifests(allTemplates), [allTemplates]);

  // Step 1 — apply category / featuredOnly filters
  const filtered = useMemo(() => {
    let result: TemplateManifest[] = allManifests;

    if (category) {
      result = result.filter((t) => t.category.some((entry) => entry === category));
    }

    if (featuredOnly) {
      result = result.filter((t) => t.featuredOrder !== undefined);
    }

    return result;
  }, [allManifests, category, featuredOnly]);

  // Step 2 — build Fuse instance from the post-filter list
  const fuse = useMemo(
    () =>
      new Fuse(filtered, {
        keys: [
          { name: 'name', weight: 2.0 },
          { name: 'tags', weight: 1.8 },
          { name: 'description', weight: 1.5 },
          { name: 'category', weight: 1.0 },
        ],
        threshold: 0.3,
        includeScore: true,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [filtered],
  );

  // Step 3 — apply fuzzy search if active, then sort.
  // When searching, preserve Fuse.js relevance ranking instead of re-sorting.
  const templates = useMemo(() => {
    if (isSearching) {
      return fuse.search(trimmedQuery).map((r) => r.item);
    }
    return sortTemplates(filtered, sortBy);
  }, [fuse, filtered, isSearching, trimmedQuery, sortBy]);

  // Step 4 — derive categories from the full manifest list (not filtered)
  const categories = useMemo<TemplateCategory[]>(() => {
    const counts = new Map<string, number>();
    for (const t of allManifests) {
      // A template can belong to multiple categories — count it under each.
      for (const c of t.category) {
        counts.set(c, (counts.get(c) ?? 0) + 1);
      }
    }

    const categoryEntries: TemplateCategory[] = TEMPLATE_CATEGORIES.map((key) => ({
      key,
      label: CATEGORY_DISPLAY[key],
      count: counts.get(key) ?? 0,
    })).filter((c) => c.count > 0);

    return [{ key: 'all', label: 'All', count: allTemplates.length }, ...categoryEntries];
  }, [allManifests, allTemplates.length]);

  // Look up template by ID from fetched data
  const getTemplate = useCallback(
    (id: string): TemplateDefinition | undefined => {
      return allTemplates.find((t) => t.id === id);
    },
    [allTemplates],
  );

  return {
    templates,
    categories,
    getTemplate,
    totalCount: allTemplates.length,
    isSearching,
    isLoading,
    error: error ?? null,
  };
}
